import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, ArrowUp, Loader2 } from 'lucide-react';
import { andreContextLine, andrePeriod } from '../../lib/andreBridge';

// The same presentation is mounted once per workspace, so page navigation
// keeps the conversation. The host decides which service supplies replies.
// `launcher`: the floating button. The workspace passes false: its page
// header already has the one Ask Andre button (UX pass 2026-10-16, A6), and
// a second floating one covered table rows. DDIR's host keeps its own.
export default function ChatPanel({ open, onOpen, onClose, context, request, getReply, sample = false, host = false, organisation = '', launcher = true }) {
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState([]);
  const [selection, setSelection] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const logRef = useRef(null);
  const returnFocus = useRef(null);
  const mounted = useRef(true);
  const scope = selection || context;

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setSelection(null); }, [context]);
  useEffect(() => {
    if (!request) return;
    setQuestion(request.prompt || '');
    setSelection(request.context || null);
    inputRef.current?.focus();
  }, [request]);
  useEffect(() => {
    if (!open) return undefined;
    returnFocus.current = document.activeElement;
    inputRef.current?.focus();
    return () => { if (returnFocus.current?.isConnected) returnFocus.current.focus(); };
  }, [open]);
  useEffect(() => { if (open && !loading) inputRef.current?.focus(); }, [loading, open]);
  useEffect(() => {
    if (open && logRef.current) logRef.current.scrollTop = history.length || loading ? logRef.current.scrollHeight : 0;
  }, [history, loading, open]);

  async function send(event) {
    event.preventDefault();
    const trimmed = question.trim();
    if (!trimmed || loading) return;
    const entry = { question: trimmed, context: { ...scope }, pending: true, time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) };
    setQuestion('');
    setHistory(previous => [...previous, entry]);
    setLoading(true);
    try {
      const reply = await getReply(trimmed, entry.context);
      if (mounted.current) setHistory(previous => previous.map(item => item === entry ? { ...entry, pending: false, ...reply } : item));
    } catch (error) {
      if (mounted.current) setHistory(previous => previous.map(item => item === entry ? { ...entry, pending: false, error: error.message || 'Andre could not answer. Please try again.' } : item));
    } finally {
      if (mounted.current) { setLoading(false); inputRef.current?.focus(); }
    }
  }

  /* Only the local demo (sample) answers with the page's driver and period.
     Production /api/chat receives the question alone, so nothing in the
     production panel may imply a selected driver or period: the question
     log carries no scope label and the starters name their own scope. */
  const scoped = sample;
  const starters = scoped && scope.driver ? [
    { label: 'Summarise findings', detail: `${scope.driver}, for the selected period`, prompt: `Summarise ${scope.driver}'s findings for this period` },
    { label: 'Check driving events', detail: 'Speeding and harsh braking records', prompt: `Show ${scope.driver}'s speeding and harsh braking events` },
  ] : scoped ? [
    { label: 'Summarise infringements', detail: 'Review the selected period', prompt: 'Summarise the infringements for this period' },
    { label: 'Check driving events', detail: 'Speeding and harsh braking by driver', prompt: 'Which drivers have speeding or harsh braking events?' },
    { label: 'Review my fleet', detail: 'Find records that need attention', prompt: 'What should I review first?' },
  ] : [
    { label: 'Summarise infringements', detail: 'Last month, across the fleet', prompt: 'Which drivers had the most infringements last month?' },
    { label: 'Check driving events', detail: 'Speeding and harsh braking, last 30 days', prompt: 'Which drivers had speeding or harsh braking events in the last 30 days?' },
    { label: 'Review my fleet', detail: 'Find records that need attention', prompt: 'What should I review first?' },
  ];

  return <>
    {!open && launcher && <button type="button" className="fab andre-launcher" onClick={onOpen} aria-haspopup="dialog" aria-expanded={false}>
      <MessageCircle size={16} aria-hidden="true" /><span>Ask Andre</span>
    </button>}
    {open && <aside className="chat-panel andre-conversation tp-andre" role="dialog" aria-label="Andre assistant" onKeyDown={event => {
      if (event.key === 'Escape') { event.stopPropagation(); onClose(); }
    }}>
      <div className="chat-head">
        <div className="andre-conversation__identity">
          <p>Andre<span>{host ? 'DDIR assistant' : 'Tachograph assistant'}</span></p>
          {organisation && <span className="andre-conversation__organisation">{organisation}</span>}
        </div>
        <div className="andre-conversation__controls">
          {history.length > 0 && <button type="button" className="andre-text-button" disabled={loading} aria-label="New conversation" onClick={() => { setHistory([]); setQuestion(''); inputRef.current?.focus(); }}>New</button>}
          <button type="button" className="andre-close" onClick={onClose} aria-label="Close Andre"><X size={18} aria-hidden="true" /></button>
        </div>
      </div>
      <div className="tp-andre-context" aria-label="Andre question context">
        <div><span>Viewing</span><strong>{andreContextLine(scope)}</strong></div>
        <p>{andrePeriod(scope)}</p>
        {selection?.driver && selection.driver !== context.driver && <button type="button" onClick={() => setSelection(null)}>Use current page instead</button>}
      </div>
      <div className="chat-body" ref={logRef} role="log" aria-label="Conversation with Andre" aria-live="polite" aria-relevant="additions text">
        {history.length === 0 && <div className="andre-start">
          <h2>What needs doing?</h2>
          <p>{scoped && scope.driver ? `Ask about ${scope.driver} and the selected records.` : 'Ask about your fleet’s tachograph records.'}</p>
          <div className="andre-start__examples" aria-label="Start a request">{starters.map(starter => <button type="button" key={starter.label} onClick={() => { setQuestion(starter.prompt); inputRef.current?.focus(); }}>
            <span><strong>{starter.label}</strong><span>{starter.detail}</span></span><span aria-hidden="true">↗</span>
          </button>)}</div>
        </div>}
        {history.map((message, index) => <React.Fragment key={index}>
          <div className="chat-msg chat-msg--user">
            <div className="chat-msg__meta"><time>{message.time}</time></div>
            <p className="tp-andre-answer">{message.question}</p>
            {scoped && <p className="tp-andre-question-context">{andreContextLine(message.context)} · {andrePeriod(message.context)}</p>}
          </div>
          <div className="chat-msg chat-msg--andre">
            <div className="chat-msg__meta"><span>Andre</span><time>{message.time}</time>{sample && <span>Sample reply</span>}</div>
            {message.pending ? <p className="andre-working" role="status">Working on your request…</p>
              : message.error ? <p className="andre-error" role="alert">{message.error}</p>
              : <><p className="tp-andre-answer">{message.answer}</p>
                {message.rows?.length > 0 && message.rows.length <= 20 && <details className="andre-records"><summary>View {message.rows.length} supporting {message.rows.length === 1 ? 'record' : 'records'}</summary>
                  <div><table><thead><tr>{Object.keys(message.rows[0]).map(key => <th key={key} scope="col">{key}</th>)}</tr></thead><tbody>{message.rows.map((row, rowIndex) => <tr key={rowIndex}>{Object.entries(row).map(([key, value]) => <td key={key}>{value == null ? '' : String(value)}</td>)}</tr>)}</tbody></table></div>
                </details>}
                {message.source && <small className="andre-source">{message.source}</small>}
                {message.truncated && <small>Results truncated. Refine your question to see more.</small>}
              </>}
          </div>
        </React.Fragment>)}
      </div>
      <form className="chat-foot" onSubmit={send}>
        <div className="andre-composer">
          <textarea aria-label="Message Andre" ref={inputRef} rows={2} value={question} maxLength={2000} onChange={event => setQuestion(event.target.value)} placeholder="Tell Andre what you need…" disabled={loading}
            onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); event.currentTarget.form.requestSubmit(); } }} />
          <button className="andre-send" type="submit" disabled={loading || !question.trim()} aria-label="Send">{loading ? <Loader2 size={19} className="animate-spin" /> : <ArrowUp size={19} />}</button>
        </div>
        <div className="andre-composer__footer"><span>Enter to send · Shift + Enter for a new line</span><span>{sample ? 'Local demo' : 'Andre'}</span></div>
        <p className="andre-demo-note">{sample ? 'Sample replies only. No AI calls, messages or changes.' : 'Selected context is not yet connected to this chat. Include names and dates in your question and check the underlying records before acting.'}</p>
      </form>
    </aside>}
  </>;
}
